import React, { useEffect, useState } from 'react'
import { complianceApi, ContactResponse } from '@/shared/api/complianceApi'
import { Mail, Clock, MapPin, Building, Loader } from 'lucide-react'

export const ContactPage: React.FC = () => {
  const [info, setInfo] = useState<ContactResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    // Dynamic SEO title
    document.title = 'Contact Support | NoTimeToDelay – Link2Earn'

    // Fetch dynamic contact properties
    complianceApi.getContactInfo()
      .then((data) => {
        setInfo(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error('Failed to load contact info:', err)
        setError('Unable to load support contact information. Please try again later.')
        setLoading(false)
      })
  }, [])

  return (
    <div 
      style={{
        width: '100%',
        maxWidth: '600px',
        margin: 'var(--space-12) auto',
        padding: '0 var(--space-6)',
      }}
    >
      <div 
        style={{
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-8)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', borderBottom: '1px solid var(--color-border)', paddingBottom: 'var(--space-4)' }}>
          <Mail size={28} style={{ color: 'var(--color-accent)' }} />
          <h1 style={{ margin: 0, fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)' }}>Contact Support</h1>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px', gap: 'var(--space-4)' }}>
            <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>Loading Support Details...</span>
          </div>
        ) : error ? (
          <div style={{ padding: 'var(--space-6)', backgroundColor: 'var(--color-error-subtle)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-md)', color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        ) : info ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Need assistance with link conversions, privacy requests, or have questions about NoTimeToDelay – Link2Earn? Contact our support desk below.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {/* Support Email Card */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ color: 'var(--color-accent)' }}>
                  <Mail size={20} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-semibold)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Support</span>
                  <a href={`mailto:${info.email}`} style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                    {info.email}
                  </a>
                </div>
              </div>

              {/* Response Time Card */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ color: 'var(--color-accent)' }}>
                  <Clock size={20} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-semibold)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expected Response Time</span>
                  <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
                    Within {info.responseTime}
                  </span>
                </div>
              </div>

              {/* Business Entity Card */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ color: 'var(--color-accent)' }}>
                  <Building size={20} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-semibold)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Entity</span>
                  <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
                    {info.businessName}
                  </span>
                </div>
              </div>

              {/* Governing Law Card */}
              <div 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--color-surface-2)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ color: 'var(--color-accent)' }}>
                  <MapPin size={20} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', fontWeight: 'var(--font-semibold)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Jurisdiction</span>
                  <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
                    {info.governingLaw}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
export default ContactPage
